import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

import { hasCallToAction } from '../utils';

import Block from './Block';
import H2 from './H2';
import H4 from './H4';
import RichTextHtml from './RichTextHtml';
import Image from './Image';
import CTAAnchor from './CTAAnchor';
import Grid from './Grid';
import Cell from './Cell';

const CardsWrapper = styled.div`
  display: flex;
  overflow: auto;
  padding: 1rem;
  width: 100%;
  scroll-snap-type: x mandatory;
  scroll-padding-left: 10px;

  overflow: -moz-scrollbars-none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    width: 0 !important;
    display: none;
  }
`;

const Card = styled(Block)`
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
  border-radius: 1rem;
  flex: 0 0 250px;
  overflow: hidden;
  margin-right: 1rem;
  display: flex;
  flex-direction: column;
  scroll-snap-align: start;

  &:last-of-type {
    margin-right: 4rem;
  }

  ${breakpoint('tablet')`
    flex: 0 0 330px;
  `};
`;

const PseudoCard = styled.div`
  flex: 0 0 100px;

  ${breakpoint('tablet')`
    flex: 0 0 200px;
  `};
`;

const Fader = styled.div`
  height: calc(100% - 60px);
  width: 100px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.001),
    rgba(255, 255, 255, 1)
  );
  position: absolute;
  top: 60px;
  right: 0;
  pointer-events: none;

  ${breakpoint('tablet')`
    width: 200px;
  `};
`;

const PageTeaserCards = ({ content }) => (
  <Block
    id={content.primary.internal_link_id}
    py={{ mobile: 'baseUp4', tablet: 'baseUp12' }}
  >
    <Grid>
      <Cell size={1} position="relative">
        <H2 px="baseUnit" mb="baseUnit">
          {content.primary.title1.text}
        </H2>

        <CardsWrapper>
          {content.items.map((item, i) => (
            <Card key={`card-${item.title1?.text}-${i}`}>
              <Block>
                <Image
                  content={item.image}
                  alt={item.image.alt}
                  objectFit="contain"
                />
              </Block>

              <Block
                p="baseUnit"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                style={{ flex: '1' }}
              >
                <Block>
                  <H4 mb="baseUnit" color="orange">
                    {item.title1.text}
                  </H4>
                  <RichTextHtml html={item.description.html} />
                </Block>

                {hasCallToAction(item) ? (
                  <Block pt="baseUp4">
                    <CTAAnchor
                      text={item.call_to_action_text.text}
                      link={item.call_to_action_link}
                      colorTheme={item.call_to_action_color}
                    />
                  </Block>
                ) : null}
              </Block>
            </Card>
          ))}
          <PseudoCard>&nbsp;</PseudoCard>
        </CardsWrapper>

        <Fader />
      </Cell>
    </Grid>
  </Block>
);

PageTeaserCards.propTypes = {
  content: PropTypes.shape({
    primary: PropTypes.shape({
      internal_link_id: PropTypes.string,
      title1: PropTypes.shape({
        text: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
};

export default PageTeaserCards;

export const query = graphql`
  fragment PageTeaserCardsFragment on PrismicPageDataBodyTeaserCards {
    id
    slice_type
    primary {
      internal_link_id
      title1 {
        text
      }
    }
    items {
      title1 {
        text
      }
      description {
        html
      }
      image {
        alt
        localFile {
          childImageSharp {
            gatsbyImageData(width: 330, height: 260)
          }
        }
      }
      call_to_action_color
      call_to_action_text {
        text
      }
      call_to_action_link {
        link_type
        url
        lang
        target
      }
    }
  }
`;
